import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { DomainsCircle } from "src/pages/reports/fsci/all/graphics/domains_circle";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type TeachingAndLearningProps = {
    readonly reports: Array<any>;
};

/*
 * Main
 */

export const TeachingAndLearning = (props: TeachingAndLearningProps): React.ReactElement => {
    const { reports } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="teachingAndLearning" inline />

                <PageTitle>Teaching &amp; Learning</PageTitle>
                <Body>
                    Schools are of course known as sites of learning for students. But if school cultures are to
                    flourish, schools and educators themselves must also engage in learning. In fact, a school culture
                    in which educators are committed to ongoing learning and improvement is linked to flourishing not
                    only for the school and educators, but also for students.
                </Body>
                <Body>
                    FSCI data analysis identified constructs related to <em>Teaching &amp; Learning</em> for five of the
                    seven survey groups (alumni; teachers; leaders/administrators; support staff; and board members), as
                    follows.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(reports[0].node.results.raw.teachingAndLearning),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={reports
                                            .map((r) => ({
                                                label: r.node.year,
                                                value: r.node.results.raw.teachingAndLearning[construct] * 100,
                                            }))
                                            .concat({
                                                label: "FSCI Benchmark",
                                                value:
                                                    reports[0].node.results.benchmarks.teachingAndLearning[construct] *
                                                    100,
                                            })}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
